import { Button, CircularProgress, Stack, TextField } from '@mui/material';
import {
  kundmarkeMaxLength,
  useKundmarkeContext,
} from '../../../common/contexts/KundmarkeContext';
import { LoadState } from '../../../types/LoadState';
import { Omvardering } from '../../types/dtos/VarderingarModel';
import { OptionalTooltip } from '../common/OptionalTooltip';

export enum VarderingMode {
  NyVardering = 'NY_VARDERING',
  Omvardering = 'OMVARDERING',
  Disabled = 'DISABLED',
}

const getVarderingsmode = (omvardering: Omvardering): VarderingMode => {
  if (!omvardering) {
    return VarderingMode.NyVardering;
  }
  return omvardering.antalOmvarderingar < omvardering.maxAntalOmvarderingar &&
    new Date(omvardering.kanOmvarderasTill) > new Date(Date.now())
    ? VarderingMode.Omvardering
    : VarderingMode.Disabled;
};

interface VarderaButtonProps {
  hasBostadsinformation: boolean;
  omvardering: Omvardering;
  loadState: LoadState;
}

export const VarderaButton: React.FC<VarderaButtonProps> = ({
  hasBostadsinformation,
  omvardering,
  loadState,
}) => {
  const { kundmarke, setKundmarke, visaKundmarke, kundmarkeKravs } =
    useKundmarkeContext();

  const mode = getVarderingsmode(omvardering);

  const label =
    mode === VarderingMode.NyVardering ? 'Köp värdering' : 'Gör om värdering';

  const getTooltipLabel = (): string => {
    if (!omvardering) {
      return '';
    }
    if (mode === VarderingMode.Disabled) {
      return 'Objektet är låst för omvärdering';
    } else {
      const antalOvarderingarKvar =
        omvardering.maxAntalOmvarderingar - omvardering.antalOmvarderingar;
      return `Objektet kan omvärderas ${antalOvarderingarKvar} ${
        antalOvarderingarKvar === 1 ? ' gång till' : ' gånger till'
      }`;
    }
  };

  return (
    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ pt: 1 }}>
      {visaKundmarke && (
        <TextField
          value={kundmarke}
          onChange={(e) => setKundmarke(e.currentTarget.value)}
          label="Kundmärke"
          required={kundmarkeKravs}
          size="small"
          sx={{ ml: 0 }}
          inputProps={{ maxLength: kundmarkeMaxLength }}
        />
      )}
      <OptionalTooltip title={getTooltipLabel()} arrow>
        <Button
          variant="contained"
          disabled={
            !hasBostadsinformation ||
            mode === VarderingMode.Disabled ||
            loadState === LoadState.Loading
          }
          sx={{ mt: '2px' }}
          type="submit"
        >
          {loadState === LoadState.Loading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <>{label}</>
          )}
        </Button>
      </OptionalTooltip>
    </Stack>
  );
};
